import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FaLock } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlanGuard = ({ children, requiredPlan = 'basic', featureName = 'esta función' }) => {
  const navigate = useNavigate();
  const [userPlan, setUserPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      // Obtener token de autenticación
      const token = localStorage.getItem('deisycfit_token');
      if (!token) {
        console.log('[PlanGuard] No token found');
        setHasAccess(false);
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/user/profile`, { 
        withCredentials: false,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const plan = response.data.subscription_plan || 'none';
      const role = response.data.role || 'user';
      setUserPlan(plan);

      console.log(`[PlanGuard] User plan from API: "${plan}", role: "${role}"`);

      // Admin siempre tiene acceso completo
      if (role === 'admin') {
        setHasAccess(true);
        return;
      }

      // Normalizar el plan del usuario (quitar espacios extra, acentos, minúsculas)
      const normalizedPlan = plan.toLowerCase().trim()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, ''); // Quitar acentos
      
      console.log(`[PlanGuard] Original plan: "${plan}"`);
      console.log(`[PlanGuard] Normalized plan: "${normalizedPlan}"`);
      
      // Función para verificar si es un plan básico (TODAS las variaciones posibles)
      const isBasicPlan = (p) => {
        const basicPatterns = [
          'basic', 'basico', 'básico', 'plan basic', 'plan basico', 'plan básico',
          'plan_basic', 'plan_basico', 'planbasic', 'planbasico'
        ];
        return basicPatterns.some(pattern => p.includes(pattern));
      };
      
      // Función para verificar si es un plan premium o superior
      const isPremiumOrHigher = (p) => {
        const premiumPatterns = [
          'premium', 'gold', 'vip', 'competitivo', 'competitive', 
          'lifetime', 'por vida', 'porvida'
        ];
        return premiumPatterns.some(pattern => p.includes(pattern));
      };
      
      // Determinar nivel del usuario
      // Premium siempre tiene acceso a todo (nivel 2)
      // Basic tiene acceso a nivel 1
      // Free tiene nivel 0
      let userLevel = 0;
      if (isPremiumOrHigher(normalizedPlan)) {
        userLevel = 2; // Premium o superior - tiene acceso a TODO
      } else if (isBasicPlan(normalizedPlan)) {
        userLevel = 1; // Básico
      } else if (normalizedPlan !== 'free' && normalizedPlan !== 'none' && normalizedPlan !== '') {
        // Si tiene CUALQUIER plan pago que no sea free, darle al menos nivel básico
        userLevel = 1;
      }
      
      // Determinar nivel requerido
      const requiredNormalized = requiredPlan.toLowerCase().trim()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      let requiredLevel = 1; // Por defecto, requiere básico
      if (isPremiumOrHigher(requiredNormalized)) {
        requiredLevel = 2;
      }

      console.log(`[PlanGuard] User level: ${userLevel}, Required level: ${requiredLevel}`);
      console.log(`[PlanGuard] Has access: ${userLevel >= requiredLevel}`);

      setHasAccess(userLevel >= requiredLevel);
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Verificando acceso...</div>;
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 py-12 px-6">
        <div className="max-w-2xl mx-auto">
          <Card className="border-4 border-red-400">
            <CardHeader className="bg-gradient-to-r from-red-500 to-orange-500 text-white text-center">
              <div className="flex justify-center mb-4">
                <FaLock className="text-6xl" />
              </div>
              <CardTitle className="text-3xl">🔒 Acceso Restringido</CardTitle>
            </CardHeader>
            <CardContent className="pt-8 text-center space-y-6">
              <p className="text-xl font-semibold text-gray-800">
                Para acceder a {featureName} necesitas al menos el <strong>Plan {requiredPlan === 'basic' ? 'Fit Evolution' : requiredPlan === 'premium' ? 'Premium' : 'Gold'}</strong>
              </p>

              {userPlan === 'none' && (
                <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6">
                  <p className="text-lg font-bold text-yellow-900 mb-4">
                    ⚠️ Actualmente NO tienes ningún plan activo
                  </p>
                  <p className="text-sm text-yellow-800 mb-4">
                    Para usar los generadores y acceder al contenido, necesitas adquirir al menos el Fit Evolution.
                  </p>
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <p className="font-bold text-lg mb-2">💰 Fit Evolution</p>
                    <p className="text-gray-600 text-sm mb-3">
                      ✅ Generador de Dietas<br/>
                      ✅ Generador de Rutinas<br/>
                      ❌ Videos (disponibles con 40% descuento)<br/>
                      ❌ Ebooks (disponibles con 40% descuento)
                    </p>
                    <p className="text-2xl font-bold text-blue-600">Precio por definir</p>
                  </div>
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-blue-500 to-blue-700 text-white font-bold w-full"
                    onClick={() => navigate('/#planes')}
                  >
                    Ver Planes Disponibles
                  </Button>
                </div>
              )}

              {userPlan === 'basic' && requiredPlan !== 'basic' && (
                <div className="bg-blue-50 border-2 border-blue-400 rounded-lg p-6">
                  <p className="text-lg font-bold text-blue-900 mb-4">
                    📊 Tienes el Fit Evolution
                  </p>
                  <p className="text-sm text-blue-800 mb-4">
                    Para acceder a {featureName}, necesitas mejorar a Plan Premium o Gold.
                  </p>
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-purple-500 to-purple-700 text-white font-bold w-full"
                    onClick={() => navigate('/#planes')}
                  >
                    Mejorar Mi Plan
                  </Button>
                </div>
              )}

              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="w-full"
              >
                ← Volver al Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return children;
};

export default PlanGuard;
